<script lang="ts">
  import './app.css';
  import { QueryClientProvider } from '@tanstack/svelte-query';
  import { getQueryClient } from '$lib/state/queryClient';
  import { ensureSession, logout as apiLogout, restoreDeviceToken } from '$lib/api/auth';
  import { fetchMe, updateMyLocale } from '$lib/api/me';
  import { session } from '$lib/state/session.svelte';
  import { locale, t } from '$lib/i18n';
  import { continuityNav } from '$lib/state/continuityNav.svelte';
  import {
    route,
    routeFromPath,
    DEFAULT_OVERVIEW_SUB,
    DEFAULT_SITES_SUB,
    DEFAULT_LEAVE_SUB,
    DEFAULT_SECURITY_SUB,
    DEFAULT_HANDBOOK_SUB,
    DEFAULT_ADMIN_CONTINUITY_SUB,
    DEFAULT_HR_CONTINUITY_SUB,
    ADMIN_CONTINUITY_SUBS,
    HR_CONTINUITY_SUBS,
    type TabId
  } from '$lib/state/route.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import { play } from '$lib/sound';
  import Login from '$lib/components/Login.svelte';
  import Shell from '$lib/components/Shell.svelte';
  import Toast from '$lib/components/ui/Toast.svelte';
  import DashboardPage from '$lib/components/pages/DashboardPage.svelte';
  import WorkLogsPage from '$lib/components/pages/WorkLogsPage.svelte';
  import BoardPage from '$lib/components/pages/BoardPage.svelte';
   import TeamBoardPage from '$lib/components/pages/TeamBoardPage.svelte';
   import FieldAttentionPage from '$lib/components/pages/FieldAttentionPage.svelte';
  import RequestsPage from '$lib/components/pages/RequestsPage.svelte';
  import HolidaysPage from '$lib/components/pages/HolidaysPage.svelte';
  import AnnouncementsPage from '$lib/components/pages/AnnouncementsPage.svelte';
  import PolicyPage from '$lib/components/pages/PolicyPage.svelte';
  import OKRsPage from '$lib/components/pages/OKRsPage.svelte';
  import OverviewPulse from '$lib/components/hr/OverviewPulse.svelte';
  import OverviewBySite from '$lib/components/hr/OverviewBySite.svelte';
  import InboxPage from '$lib/components/hr/InboxPage.svelte';
  import StatsPage from '$lib/components/hr/StatsPage.svelte';
  import OverviewFlags from '$lib/components/hr/OverviewFlags.svelte';
  import PersonPage from '$lib/components/hr/PersonPage.svelte';
  import RemotePunchesPage from '$lib/components/hr/RemotePunchesPage.svelte';
  import PunchAlertsPage from '$lib/components/hr/PunchAlertsPage.svelte';
  import PushSettingsPage from '$lib/components/pages/PushSettingsPage.svelte';
  import UsersPage from '$lib/components/manage/UsersPage.svelte';
  import SitesPage from '$lib/components/manage/SitesPage.svelte';
  import AssignmentsPage from '$lib/components/manage/AssignmentsPage.svelte';
  import DevicesPage from '$lib/components/manage/DevicesPage.svelte';
  import LeaveTypesPage from '$lib/components/manage/LeaveTypesPage.svelte';
  import HousekeepingPage from '$lib/components/manage/HousekeepingPage.svelte';
  import LoginActivityPage from '$lib/components/manage/LoginActivityPage.svelte';
  import AuditPage from '$lib/components/manage/AuditPage.svelte';
  import ApiKeysPage from '$lib/components/manage/ApiKeysPage.svelte';
  import ContinuityHome from '$lib/components/continuity/ContinuityHome.svelte';
  import PeopleList from '$lib/components/continuity/PeopleList.svelte';
  import PlaybookPage from '$lib/components/continuity/PlaybookPage.svelte';
  import HRMyActions from '$lib/components/continuity/HRMyActions.svelte';
  import HRConversationsDue from '$lib/components/continuity/HRConversationsDue.svelte';
  import HRTrends from '$lib/components/continuity/HRTrends.svelte';

  const queryClient = getQueryClient();

  let isAuthenticated = $state(false);
  let isBootstrapping = $state(true);
  let bootError = $state('');
  const activeTab = $derived(route.current.tab);

  // A bare landing (no hash, or just "#"/"#/") is what parseHash falls back
  // to 'today' for — as opposed to a hash the user (or a shared link)
  // explicitly set to "#/today", which must NOT be redirected away from.
  function isBareLanding(): boolean {
    return location.hash === '' || location.hash === '#' || location.hash === '#/';
  }

  // Tabs in the sidenav's HR group — hr and admin only. Continuity is here
  // because hr has its own (restricted) half of it; the ADMIN-only sub-nav
  // is handled by the role-split check further down.
  function isHRTab(tab: TabId): boolean {
    return (
      (tab === 'overview' && !session.isFieldScoped) ||
      tab === 'approvals' ||
      tab === 'directory' ||
      tab === 'continuity' ||
      tab === 'sites' ||
      tab === 'leave' ||
      tab === 'remote'
    );
  }

  // Tabs in the sidenav's ADMIN group — admin only, hr gets bounced.
  function isAdminTab(tab: TabId): boolean {
    return tab === 'security';
  }

  // Kept under its original name because the safety-net $effect and
  // normalizeRouteAfterAuth both read "is this route off-limits to an
  // employee?" — which is now "HR group or Admin group".
  function isHROrAdminTab(tab: TabId): boolean {
    return isHRTab(tab) || isAdminTab(tab);
  }

  function defaultContinuitySub() {
    return session.isAdmin ? DEFAULT_ADMIN_CONTINUITY_SUB : DEFAULT_HR_CONTINUITY_SUB;
  }

   function isTeamBoardTab(tab: TabId): boolean {
    return tab === 'team_board';
   }
   function isFieldTab(tab: TabId): boolean { return tab === 'field'; }

  function normalizeRouteAfterAuth() {
    const r = route.current;
    // employee -> bounced off every HR/Admin route; hr -> bounced off the
    // Admin-only ones. Both land on Today.
    if (isHROrAdminTab(r.tab) && !session.isHROrAdmin) {
      route.replace({ tab: 'today' });
    } else if (isFieldTab(r.tab) && !session.canViewField) {
      route.replace({ tab: 'today' });
    } else if (isAdminTab(r.tab) && !session.isAdmin) {
      route.replace({ tab: 'today' });
    } else if (isTeamBoardTab(r.tab) && !session.canViewTeamBoard) {
      route.replace({ tab: 'today' });
    } else if (
      r.tab === 'continuity' &&
      ((session.isAdmin && !ADMIN_CONTINUITY_SUBS.includes(r.sub)) || (!session.isAdmin && !HR_CONTINUITY_SUBS.includes(r.sub)))
    ) {
      // A deep link (or role switch) landed on the other role's sub-nav —
      // bounce to this role's default instead of rendering nothing.
      route.replace({ tab: 'continuity', sub: defaultContinuitySub() });
    } else if (r.tab === 'today' && isBareLanding() && session.isHROrAdmin) {
      // hr/admin get the org view by default; the personal punch dashboard
      // stays reachable as its own tab, they just don't land there.
      route.replace({ tab: 'overview', sub: DEFAULT_OVERVIEW_SUB });
    } else {
      route.replace(route.current);
    }
  }

  async function bootstrap() {
    isBootstrapping = true;
    bootError = '';
    try {
      let me = await fetchMe();
      if (locale.changedBeforeLogin && locale.current !== me.locale) {
        try {
          me = await updateMyLocale(locale.current);
          locale.clearLoginOverride();
        } catch {
          locale.applyFromUser(me.locale);
        }
      } else {
        locale.applyFromUser(me.locale);
      }
      session.setMe(me);
      // login() already restores the device token for the fresh-login path;
      // this covers the page-reload path (silent refresh-token exchange).
      restoreDeviceToken(me.email);
      isAuthenticated = true;
      normalizeRouteAfterAuth();
    } catch (err) {
      bootError = getErrorMessage(err);
      isAuthenticated = false;
    } finally {
      isBootstrapping = false;
    }
  }

  async function checkAuth() {
    const ok = await ensureSession();
    if (ok) {
      await bootstrap();
    } else {
      isBootstrapping = false;
    }
  }

  async function handleAuthenticated() {
    await bootstrap();
  }

  function logout() {
    void apiLogout().catch((err) => console.warn('Notification tracking logout failed:', getErrorMessage(err)));
    play('ready');
    session.clear();
    isAuthenticated = false;
    queryClient.clear();
    route.replace({ tab: 'today' });
  }

  function navigate(tab: TabId) {
    if (tab === 'overview') route.push({ tab: 'overview', sub: DEFAULT_OVERVIEW_SUB });
    else if (tab === 'sites') route.push({ tab: 'sites', sub: DEFAULT_SITES_SUB });
    else if (tab === 'leave') route.push({ tab: 'leave', sub: DEFAULT_LEAVE_SUB });
    else if (tab === 'security') route.push({ tab: 'security', sub: DEFAULT_SECURITY_SUB });
    else if (tab === 'handbook') route.push({ tab: 'handbook', sub: DEFAULT_HANDBOOK_SUB });
    else if (tab === 'directory') route.push({ tab: 'directory' });
    else if (tab === 'continuity') route.push({ tab: 'continuity', sub: defaultContinuitySub() });
    else
      route.push({
        tab: tab as Exclude<TabId, 'overview' | 'sites' | 'leave' | 'security' | 'handbook' | 'directory' | 'continuity'>
      });
  }

  // Cross-tab jump-to-OKR / jump-to-conversations-overdue (see
  // continuityNav.svelte.ts) both navigate then rely on the target page
  // reading the one-shot flag itself.
  function goToOKRs(filter: 'red' | 'never_proven') {
    continuityNav.okrQuickFilter = filter;
    continuityNav.jumpToOKRId = null;
    route.push({ tab: 'continuity', sub: 'okrs' });
  }
  function jumpToOKR(id: number) {
    continuityNav.jumpToOKRId = id;
    continuityNav.okrQuickFilter = null;
    route.push({ tab: 'continuity', sub: 'okrs' });
  }
  function goToPeopleOverdue() {
    continuityNav.peopleOverdueOnly = true;
    route.push({ tab: 'continuity', sub: 'conversations' });
  }

  // Safety net: a non-admin/non-hr that edits the hash directly (or hits
  // Back/Forward onto a stale entry) still can't reach People/Workplace
  // tabs — every gated route also 403s server-side (middleware.RequireAdmin/
  // RequireHR).
  $effect(() => {
    if (!isAuthenticated) return;
    const r = route.current;
    if (isHROrAdminTab(r.tab) && !session.isHROrAdmin) {
      route.replace({ tab: 'today' });
    } else if (isAdminTab(r.tab) && !session.isAdmin) {
      route.replace({ tab: 'today' });
    } else if (isTeamBoardTab(r.tab) && !session.canViewTeamBoard) {
      route.replace({ tab: 'today' });
    } else if (isFieldTab(r.tab) && !session.canViewField) {
      route.replace({ tab: 'today' });
    } else if (
      r.tab === 'continuity' &&
      ((session.isAdmin && !ADMIN_CONTINUITY_SUBS.includes(r.sub)) || (!session.isAdmin && !HR_CONTINUITY_SUBS.includes(r.sub)))
    ) {
      route.replace({ tab: 'continuity', sub: defaultContinuitySub() });
    }
  });

  checkAuth();

  // The service worker's notificationclick handler focuses this tab (rather
  // than opening a new one) when it's already open, and posts the target
  // route back to us since focus() alone can't navigate an existing page.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'notification-navigate' && event.data.route) {
        route.push(routeFromPath(event.data.route));
      }
    });
  }
</script>

<svelte:head>
  <title>Teams</title>
</svelte:head>

<QueryClientProvider client={queryClient}>
  <Toast />

  {#if isBootstrapping}
    <div class="flex min-h-screen items-center justify-center text-gray-500">{t('app.loading')}</div>
  {:else if !isAuthenticated}
    <Login onAuthenticated={handleAuthenticated} />
  {:else}
    <Shell {activeTab} onNavigate={navigate} onLogout={logout}>
      {#if bootError}
        <div class="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {bootError}
        </div>
      {/if}
      {#if activeTab === 'today'}
        <DashboardPage />
      {:else if activeTab === 'board'}
        <BoardPage />
      {:else if activeTab === 'team_board'}
        <TeamBoardPage />
      {:else if activeTab === 'my_requests'}
        <RequestsPage />
      {:else if activeTab === 'history'}
        <WorkLogsPage />
      {:else if activeTab === 'handbook'}
        {@const sub = route.current.tab === 'handbook' ? route.current.sub : DEFAULT_HANDBOOK_SUB}
        {#if sub === 'announcements'}
          <AnnouncementsPage />
        {:else if sub === 'rules'}
          <PolicyPage slug="rules" />
        {:else if sub === 'policy'}
          <PolicyPage slug="policy" />
        {:else if sub === 'holidays'}
          <HolidaysPage />
        {/if}
      {:else if activeTab === 'overview' && (session.isHROrAdmin || session.isFieldScoped)}
        {@const sub = route.current.tab === 'overview' ? route.current.sub : DEFAULT_OVERVIEW_SUB}
        {#if sub === 'pulse'}
          <OverviewPulse />
        {:else if sub === 'sites'}
          <OverviewBySite />
        {:else if sub === 'stats'}
          <StatsPage />
        {:else if sub === 'flags'}
          <OverviewFlags />
        {/if}
      {:else if activeTab === 'approvals' && session.isHROrAdmin}
        <InboxPage />
      {:else if activeTab === 'remote' && session.isHROrAdmin}
        <RemotePunchesPage />
      {:else if activeTab === 'alerts'}
        {#if session.isHROrAdmin}
          <PunchAlertsPage />
        {:else}
          <PushSettingsPage />
        {/if}
      {:else if activeTab === 'directory' && session.isHROrAdmin}
        {#if route.current.tab === 'directory' && route.current.sub === 'person'}
          <PersonPage userId={route.current.userId} onJumpToOKR={jumpToOKR} />
        {:else}
          <UsersPage />
        {/if}
      {:else if activeTab === 'continuity' && session.isHROrAdmin}
        {@const sub = route.current.tab === 'continuity' ? route.current.sub : defaultContinuitySub()}
        {#if session.isAdmin}
          {#if sub === 'risk'}
            <ContinuityHome onGoOKRs={goToOKRs} onGoPeople={goToPeopleOverdue} onStarted={jumpToOKR} />
          {:else if sub === 'okrs'}
            <OKRsPage jumpToId={continuityNav.jumpToOKRId} initialFilter={continuityNav.okrQuickFilter} />
          {:else if sub === 'conversations'}
            <PeopleList initialOverdueOnly={continuityNav.peopleOverdueOnly} />
          {:else if sub === 'playbook'}
            <PlaybookPage />
          {/if}
        {:else if sub === 'my_actions'}
          <HRMyActions />
        {:else if sub === 'conversations_due'}
          <HRConversationsDue />
        {:else if sub === 'trends'}
          <HRTrends />
        {/if}
      {:else if activeTab === 'sites' && session.isHROrAdmin}
        {@const sub = route.current.tab === 'sites' ? route.current.sub : DEFAULT_SITES_SUB}
        {#if sub === 'sites'}
          <SitesPage />
        {:else if sub === 'assignments'}
          <AssignmentsPage />
        {:else if sub === 'devices'}
          <DevicesPage />
        {/if}
      {:else if activeTab === 'leave' && session.isHROrAdmin}
        {@const sub = route.current.tab === 'leave' ? route.current.sub : DEFAULT_LEAVE_SUB}
        {#if sub === 'types'}
          <LeaveTypesPage />
        {:else if sub === 'holidays'}
          <HolidaysPage />
        {:else if sub === 'housekeeping'}
          <HousekeepingPage />
        {/if}
      {:else if activeTab === 'security' && session.isAdmin}
        {@const sub = route.current.tab === 'security' ? route.current.sub : DEFAULT_SECURITY_SUB}
        {#if sub === 'api-keys'}
          <ApiKeysPage />
        {:else if sub === 'logins'}
          <LoginActivityPage />
        {:else if sub === 'audit'}
          <AuditPage />
        {/if}
      {:else if activeTab === 'field' && session.canViewField}
        <FieldAttentionPage />
      {/if}
    </Shell>
  {/if}
</QueryClientProvider>
